import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { HwcgActivationUserTimeline } from './HwcgActivationUserTimeline';
import {
  channelLabel,
  eventKindLabel,
  filterActivationUsers,
  formatLogTime,
  normalizeStep2Response,
  rosterStatusLabel,
  sourceLabel,
  type HwcgActivationEvent,
  type HwcgActivationLogFilters,
  type HwcgActivationUserLog,
} from './hwcgActivationLogModel';

function statusVariant(status: HwcgActivationUserLog['status']) {
  if (status === 'activated') return 'green' as const;
  if (status === 'declined') return 'destructive' as const;
  if (status === 'pending') return 'amber' as const;
  return 'secondary' as const;
}

function lastReply(user: HwcgActivationUserLog): string {
  if (user.step2Response) {
    return `${user.step2Response} · ${formatLogTime(user.step2RespondedAt)}`;
  }
  if (user.step1Response) {
    return `${user.step1Response} · ${formatLogTime(user.step1RespondedAt)}`;
  }
  return user.notices.length > 0 ? 'Awaiting' : '—';
}

function channelsUsed(user: HwcgActivationUserLog): string {
  const channels = new Set<string>();
  for (const notice of user.notices) {
    for (const attempt of notice.attempts) channels.add(attempt.channel);
  }
  return channels.size === 0
    ? '—'
    : [...channels].map((channel) => channelLabel(channel)).join(', ');
}

function EventsList({ events }: { events: HwcgActivationEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        No activity recorded yet.
      </p>
    );
  }
  return (
    <ol className="tw:m-0 tw:flex tw:max-h-72 tw:list-none tw:flex-col tw:gap-1 tw:overflow-y-auto tw:p-0 tw:text-xs">
      {events.map((event, index) => (
        <li
          key={`${event.at}-${event.userGuid}-${event.kind}-${index}`}
          className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:border-b tw:border-border/60 tw:py-1 last:tw:border-b-0"
        >
          <span className="tw:w-32 tw:shrink-0 tw:tabular-nums tw:text-muted-foreground">
            {formatLogTime(event.at)}
          </span>
          <span className="tw:font-medium">{event.name}</span>
          <span>
            {eventKindLabel(event.kind)}
            {event.channel ? ` via ${channelLabel(event.channel)}` : ''}
            {event.step ? ` (step ${event.step})` : ''}
          </span>
          {event.positionName ? (
            <span className="tw:text-muted-foreground">
              · {event.positionName}
            </span>
          ) : null}
          {event.detail ? (
            <span className="tw:text-muted-foreground">— {event.detail}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * Every person in the incident's activation log with filters by source, step
 * and roster status; a row expands into that person's notice timeline. The
 * optional events tab lists the raw send / delivery / reply / status feed.
 */
export function HwcgActivationLogTable({
  users,
  events,
  isLoading = false,
  isError = false,
}: {
  users: HwcgActivationUserLog[] | undefined;
  events?: HwcgActivationEvent[];
  isLoading?: boolean;
  isError?: boolean;
}) {
  const [filters, setFilters] = useState<HwcgActivationLogFilters>({
    source: 'all',
    status: 'all',
    step: 'all',
    search: '',
  });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [view, setView] = useState<'people' | 'events'>('people');

  const filtered = useMemo(
    () => filterActivationUsers(users ?? [], filters),
    [users, filters]
  );

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Loading activation log…
      </p>
    );
  }
  if (isError) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-destructive">
        Could not load the activation log.
      </p>
    );
  }

  return (
    <section
      className="tw:space-y-3"
      aria-label="Activation log"
      data-tour="hwcg-activation-log"
    >
      <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-2">
        <div className="tw:flex tw:items-center tw:gap-1">
          <Button
            type="button"
            size="sm"
            variant={view === 'people' ? 'default' : 'outline'}
            onClick={() => setView('people')}
          >
            People
          </Button>
          <Button
            type="button"
            size="sm"
            variant={view === 'events' ? 'default' : 'outline'}
            onClick={() => setView('events')}
          >
            Events{events ? ` (${events.length})` : ''}
          </Button>
        </div>
        {view === 'people' ? (
          <>
            <Input
              value={filters.search ?? ''}
              placeholder="Search name, email, position"
              aria-label="Search activation log"
              className="tw:h-8 tw:max-w-xs"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
            <Select
              value={filters.source ?? 'all'}
              onValueChange={(value) =>
                setFilters((current) => ({
                  ...current,
                  source: (value ?? 'all') as HwcgActivationLogFilters['source'],
                }))
              }
            >
              <SelectTrigger className="tw:h-8 tw:min-w-[150px]" aria-label="Source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="roster">Roster seat</SelectItem>
                <SelectItem value="group">Group action</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={String(filters.step ?? 'all')}
              onValueChange={(value) =>
                setFilters((current) => ({
                  ...current,
                  step:
                    value === '1' ? 1 : value === '2' ? 2 : ('all' as const),
                }))
              }
            >
              <SelectTrigger className="tw:h-8 tw:min-w-[150px]" aria-label="Step">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="all">All steps</SelectItem>
                <SelectItem value="1">Step 1 sent</SelectItem>
                <SelectItem value="2">Step 2 sent</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status ?? 'all'}
              onValueChange={(value) =>
                setFilters((current) => ({
                  ...current,
                  status: (value ?? 'all') as HwcgActivationLogFilters['status'],
                }))
              }
            >
              <SelectTrigger className="tw:h-8 tw:min-w-[150px]" aria-label="Roster status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="activated">Activated</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </>
        ) : null}
      </div>

      {view === 'events' ? (
        <EventsList events={events ?? []} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-8" />
              <TableHead>Name</TableHead>
              <TableHead>Position(s)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Step 1</TableHead>
              <TableHead>Step 2</TableHead>
              <TableHead>Last reply</TableHead>
              <TableHead>Channels</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="tw:text-xs tw:text-muted-foreground"
                >
                  {users && users.length > 0
                    ? 'No people match these filters.'
                    : 'No activation notices have been sent for this incident yet.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((user) => {
                const open = Boolean(expanded[user.userGuid]);
                const sources = new Set(user.notices.map((n) => n.source));
                return (
                  <Fragment key={user.userGuid}>
                    <TableRow
                      className="tw:cursor-pointer"
                      onClick={() =>
                        setExpanded((current) => ({
                          ...current,
                          [user.userGuid]: !open,
                        }))
                      }
                    >
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={open ? `Collapse ${user.name}` : `Expand ${user.name}`}
                          aria-expanded={open}
                        >
                          {open ? <ChevronDown /> : <ChevronRight />}
                        </Button>
                      </TableCell>
                      <TableCell className="tw:font-medium">
                        {user.name}
                        {user.email ? (
                          <span className="tw:block tw:text-[11px] tw:font-normal tw:text-muted-foreground">
                            {user.email}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="tw:text-xs">
                        {user.positions.length > 0
                          ? user.positions.map((p) => p.positionName).join(', ')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(user.status)} className="tw:font-normal">
                          {rosterStatusLabel(user.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="tw:text-xs">
                        {user.step1Response ??
                          (user.notices.some((n) => n.step === 1) ? 'Awaiting' : '—')}
                      </TableCell>
                      <TableCell className="tw:text-xs">
                        {user.notices.some((n) => n.step === 2)
                          ? normalizeStep2Response(user.step2Response)
                          : '—'}
                      </TableCell>
                      <TableCell className="tw:text-xs">{lastReply(user)}</TableCell>
                      <TableCell className="tw:text-xs">{channelsUsed(user)}</TableCell>
                      <TableCell className="tw:text-xs">
                        {sources.size === 0
                          ? '—'
                          : [...sources].map((source) => sourceLabel(source)).join(', ')}
                      </TableCell>
                    </TableRow>
                    {open ? (
                      <TableRow>
                        <TableCell colSpan={9} className="tw:bg-muted/20">
                          <HwcgActivationUserTimeline user={user} showHeader={false} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

export default HwcgActivationLogTable;
